import type React from 'react';
import { type Ref, useRef } from 'react';
import { cn } from '@/lib/utils';
import GeneralInput, {
  type GeneralInputRef,
} from '@/shared/components/ui/GeneralInput/GeneralInput';
import { Label } from '@/shared/components/ui/label';
import FileInput from '../FileInput/FileInput';
import PasswordInput from '../PasswordInput/PasswordInput';
import type { PasswordInputRef } from '../PasswordInput/PasswordInput.types';
import Select from '../Select/Select';
import TextArea from '../TextArea/TextArea';
import type { DetailFormProps } from './DetailForm.types';

const DetailForm = ({
  direction = 'row',
  styleLabel = 'w-[170px]',
  children,
  showTitle = true,
  showInput = true,
  label,
  placeholder,
  inputType,
  value,
  defaultValue,
  className,
  selectClassName,
  disabled,
  onChange,
  optionSelect = [],
  required,
  name,
  maxLength,
  readonly,
  pattern,
  formRef,
  labelChildren,
  inputFormStyle,
  styleTextArea,
  removeFile,
}: DetailFormProps): JSX.Element => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleRemoveFile = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }

    if (removeFile) {
      removeFile();
    }
  };

  return (
    <div
      className={cn(
        'flex w-full',
        direction === 'row' ? 'flex-row items-start gap-x-4' : 'flex-col gap-y-2',
        className
      )}
    >
      {showTitle && (
        <div className="flex">
          <Label
            className={cn(
              ' ',
              direction === 'row'
                ? 'title3medium text-au-neutral-4'
                : 'text-[clamp(14px,.9vw,16px)] text-[#666666] dark:text-white uppercase w-full! ',
              styleLabel
            )}
          >
            {label}
            {required && <span className="text-[#e31616]">*</span>}
            {labelChildren}
          </Label>
        </div>
      )}

      {showInput && (
        <div className="flex-1 min-w-0 w-full">
          {inputType === 'select' ? (
            <Select
              options={optionSelect}
              value={value as string | undefined}
              defaultValue={defaultValue}
              name={name || ''}
              disabled={disabled}
              onChange={onChange}
              className={`${direction === 'row' ? selectClassName : 'bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! h-[45px]! text-[clamp(16px,1vw,20px)]!'}`}
              disableEmptyOption={true}
            />
          ) : inputType === 'textarea' ? (
            <TextArea
              // className="w-full h-full"
              disabled={disabled}
              value={value as string | undefined}
              name={name}
              onChange={onChange as (e: React.ChangeEvent<HTMLTextAreaElement>) => void}
              placeholder=""
              className={`w-full h-full overflow-y-auto resize-none   ${direction === 'row' ? '' : 'bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! text-[clamp(16px,1vw,20px)]! '}, ${styleTextArea}`}
            />
          ) : inputType === 'file' ? (
            <FileInput
              ref={inputFileRef}
              name={name ?? ''}
              onChange={onChange}
              handleRemoveFile={handleRemoveFile}
              required={false}
              disabled={disabled}
              value={value as string | undefined}
              pattern="image/*"
            />
          ) : inputType === 'password' ? (
            <PasswordInput
              ref={formRef as Ref<PasswordInputRef>}
              name={name}
              value={value as string}
              required={required}
              disabled={disabled}
              onChange={onChange}
              maxLength={maxLength}
              pattern={pattern instanceof RegExp ? pattern.source : pattern}
              placeholder={placeholder}
              inputFormStyle={inputFormStyle}
            />
          ) : (
            <GeneralInput
              ref={formRef as Ref<GeneralInputRef>}
              type={inputType}
              name={name}
              value={value}
              required={required}
              customInput={`flex flex-col gap-2 w-full`}
              disabled={disabled}
              onChange={onChange}
              maxLength={maxLength}
              readonly={readonly}
              pattern={pattern instanceof RegExp ? pattern.source : pattern}
              placeholder={placeholder}
              inputFormStyle={`${direction === 'row' ? inputFormStyle : 'bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! h-[45px]! text-[clamp(16px,1vw,20px)]!'}`}
            />
          )}
        </div>
      )}

      {children != null && (
        <div className={!showInput ? 'flex-1 min-w-0' : undefined}>{children}</div>
      )}
    </div>
  );
};

export default DetailForm;
